import { useMemo, useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort, today, addDays } from "../format.js";
import {
  Async, Badge, Button, Card, Chips, Empty, ErrorPanel, Field, Fields, Grid, Input, KPI, Modal,
  Select, Sep, Table, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "viewed", label: "Viewed" },
  { key: "accepted", label: "Accepted" },
  { key: "declined", label: "Declined" },
];

const blankLine = () => ({ key: Math.random().toString(36).slice(2), description: "", qty: 1, rate: 0, taxable: true });

export default function Estimates() {
  const { settings, reloadApp, me } = useApp();
  const canManage = !!me.permissions["estimates.manage"];
  const toast = useToast();
  const [status, setStatus] = useState("all");
  const state = useAsync(() => api.get(`estimates?status=${status}`), [status]);
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(null);
  const [error, setError] = useState(null);
  const sym = settings.currency_symbol;

  async function convert(est) {
    setError(null);
    setConverting(est.id);
    try {
      const invoice = await api.post(`estimates/${est.id}/convert`, {});
      toast.push(`${est.number} became ${invoice.number}`);
      navigate(`/invoices/${invoice.id}`);
    } catch (e) {
      setError(e);
      setConverting(null);
    }
  }

  return (
    <>
      <TopBar crumb="Get paid" title="Estimates">
        {canManage && (
          <Button kind="pri" onClick={() => setCreating(true)}>
            ＋ New estimate
          </Button>
        )}
      </TopBar>

      <Wrap>
        {error && <ErrorPanel error={error} />}
        <Async state={state}>
          {(d) => {
            const list = d.list || [];
            const s = d.stats || {};
            const won = Number(s.accepted_count || 0);
            const lost = Number(s.declined_count || 0);
            const decided = won + lost;

            return (
              <>
                <Grid cols={4}>
                  <KPI
                    label="Awaiting decision"
                    value={money(s.outstanding, sym)}
                    sub={`${Number(s.outstanding_count || 0)} estimate${
                      Number(s.outstanding_count) === 1 ? "" : "s"
                    } out`}
                  />
                  <KPI label="Accepted" value={money(s.accepted, sym)} sub={`${won} accepted to date`} />
                  <KPI
                    label="Win rate"
                    value={decided ? `${Math.round((won / decided) * 100)}%` : "—"}
                    sub={decided ? `${won} won · ${lost} declined` : "no decisions yet"}
                  />
                  <KPI label="Average estimate" value={money(s.avg_total, sym)} sub="across every estimate" />
                </Grid>

                <Card
                  className="mt"
                  bare
                  title="All estimates"
                  right={<Chips options={FILTERS} value={status} onChange={setStatus} />}
                >
                  <Table
                    rows={list}
                    onRowClick={(e) => navigate(`/estimates/${e.id}`)}
                    empty={<Empty icon="▢">No {status === "all" ? "" : status} estimates</Empty>}
                    columns={[
                      { key: "number", label: "Estimate", render: (e) => <b className="num">{e.number}</b> },
                      { key: "client_name", label: "Client" },
                      {
                        key: "title",
                        label: "Title",
                        render: (e) => <span className="sm">{e.title || <span className="muted">—</span>}</span>,
                      },
                      {
                        key: "issue_date",
                        label: "Issued",
                        render: (e) => <span className="sm dim">{fmtDateShort(e.issue_date)}</span>,
                      },
                      {
                        key: "expires_on",
                        label: "Expires",
                        render: (e) => <span className="sm dim">{fmtDateShort(e.expires_on)}</span>,
                      },
                      {
                        key: "total",
                        label: "Total",
                        align: "right",
                        render: (e) => <span className="num">{money(e.total, sym)}</span>,
                      },
                      { key: "status", label: "Status", render: (e) => <Badge status={e.status} /> },
                      {
                        key: "action",
                        label: "",
                        align: "right",
                        render: (e) =>
                          e.status === "accepted" && !e.converted_invoice_id && canManage ? (
                            <Button
                              sm
                              kind="gho"
                              busy={converting === e.id}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                convert(e);
                              }}
                            >
                              Convert →
                            </Button>
                          ) : (
                            <span className="muted">›</span>
                          ),
                      },
                    ]}
                  />
                </Card>
              </>
            );
          }}
        </Async>
      </Wrap>

      {creating && (
        <NewEstimateModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            state.reload();
            reloadApp();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- new estimate */

function NewEstimateModal({ onClose, onSaved }) {
  const { clients, items, settings } = useApp();
  const toast = useToast();
  const activeClients = clients.filter((c) => c.status === "active");

  const [form, setForm] = useState({
    client_id: activeClients[0]?.id || "",
    title: "",
    issue_date: today(),
    expires_on: addDays(today(), 30),
    notes: "",
  });
  const [lines, setLines] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const sym = settings.currency_symbol;
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const editLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) =>
    setLines((ls) => {
      const next = ls.filter((l) => l.key !== key);
      return next.length ? next : [blankLine()];
    });

  const applyItem = (key, itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (item) editLine(key, { description: item.name, rate: item.rate, taxable: item.taxable });
  };

  const totals = useMemo(() => {
    const amount = (l) => Number(l.qty || 0) * Number(l.rate || 0);
    const subtotal = lines.reduce((s, l) => s + amount(l), 0);
    const tax = (lines.filter((l) => l.taxable).reduce((s, l) => s + amount(l), 0) * Number(settings.tax_rate || 0)) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [lines, settings.tax_rate]);

  async function save() {
    setError(null);
    if (!form.client_id) return setError(new Error("Pick a client first."));
    setBusy(true);
    try {
      const est = await api.post("estimates", {
        ...form,
        lines: lines
          .filter((l) => String(l.description).trim() || Number(l.qty) || Number(l.rate))
          .map((l) => ({
            description: l.description,
            qty: Number(l.qty) || 0,
            rate: Number(l.rate) || 0,
            taxable: !!l.taxable,
          })),
      });
      toast.push(`${est.number} created as a draft`);
      onSaved();
      onClose();
      navigate(`/estimates/${est.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New estimate"
      width={720}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            Create estimate
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}

        <Fields cols={2}>
          <Field label="Client">
            <Select value={form.client_id} onChange={(e) => set("client_id", e.target.value)}>
              <option value="">Select a client…</option>
              {activeClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Title">
            <Input
              value={form.title}
              placeholder="Phase 2 — commerce build"
              onChange={(e) => set("title", e.target.value)}
            />
          </Field>
        </Fields>

        <Fields cols={2}>
          <Field label="Issue date">
            <Input type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} />
          </Field>
          <Field label="Expires on">
            <Input type="date" value={form.expires_on} onChange={(e) => set("expires_on", e.target.value)} />
          </Field>
        </Fields>

        <Field label="Line items">
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ width: 130 }}>Service</th>
                  <th className="right" style={{ width: 70 }}>
                    Qty
                  </th>
                  <th className="right" style={{ width: 90 }}>
                    Rate
                  </th>
                  <th style={{ width: 44 }}>Tax</th>
                  <th className="right" style={{ width: 96 }}>
                    Amount
                  </th>
                  <th style={{ width: 30 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key}>
                    <td>
                      <Input
                        value={l.description}
                        placeholder="What are you quoting?"
                        onChange={(e) => editLine(l.key, { description: e.target.value })}
                      />
                    </td>
                    <td>
                      <Select value="" onChange={(e) => applyItem(l.key, e.target.value)}>
                        <option value="">Custom…</option>
                        {items.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <Input
                        className="i right num"
                        value={l.qty}
                        onChange={(e) => editLine(l.key, { qty: e.target.value })}
                      />
                    </td>
                    <td>
                      <Input
                        className="i right num"
                        value={l.rate}
                        onChange={(e) => editLine(l.key, { rate: e.target.value })}
                      />
                    </td>
                    <td className="right">
                      <input
                        type="checkbox"
                        checked={!!l.taxable}
                        onChange={(e) => editLine(l.key, { taxable: e.target.checked })}
                      />
                    </td>
                    <td className="right num">{money(Number(l.qty || 0) * Number(l.rate || 0), sym)}</td>
                    <td className="right">
                      <span className="x" style={{ fontSize: 16 }} onClick={() => removeLine(l.key)}>
                        ×
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row mt">
            <Button sm onClick={() => setLines((ls) => [...ls, blankLine()])}>
              ＋ Add line
            </Button>
            <div className="grow" />
            <span className="sm muted">
              {settings.tax_label} {money(totals.tax, sym)}
            </span>
            <b className="num">{money(totals.total, sym)}</b>
          </div>
        </Field>

        <Sep />
        <Field label="Notes" hint="Shown at the bottom of the estimate the client sees.">
          <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </Fields>
    </Modal>
  );
}
